import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router";

import { useSessionRoll } from "@/components/attendance/use-session-roll";
import type { Box } from "@/components/ui/box.schema";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";

import type { ReviewWorkState } from "../review/ReviewWork";
import { reviewWork } from "../review/review-plan";
import { ReviewHost } from "./SessionReviewHost";

type RowOpenChange = (box: string, row: string, open: boolean) => void;
export type Report = { boxes: Box[]; onRowOpenChange: RowOpenChange };

/**
 * S05 Review on the session page (board 3): this session's work only, each
 * piece a box after Close, judged in place and in batches. Close ranks first;
 * an attempt opened by hand owns the page's act while it stays open. No
 * attempt opens by itself here, only by hand or Prepare's link, so a long
 * class's Review keeps its open row's commit in view (O4).
 */
export function useSessionReviewBoxes({ workspace, shown, closeRanks, onOwnsAct }: {
  workspace: SessionWorkspace;
  shown: boolean;
  /** Close's act is the page's filled act until an attempt is opened by hand. */
  closeRanks: boolean;
  onOwnsAct: (owns: boolean) => void;
}): { boxes: Box[]; hosts: ReactNode; onRowOpenChange: RowOpenChange } {
  const { session, course } = workspace;
  const works = useMemo(() => (shown ? reviewWork(workspace) : []), [shown, workspace]);
  // An older attempt may still be in-class after the current kind changed: the same cached roll Close reads.
  const roll = useSessionRoll(session.id, shown && session.status !== "cancelled");
  const marks = useMemo(() => (roll.data ? new Map(roll.data.roster.map((row) => [row.student_id, row.status])) : null), [roll.data]);
  const [states, setStates] = useState<Record<string, ReviewWorkState>>({});
  const onState = useCallback((id: string, state: ReviewWorkState) => setStates((all) => ({ ...all, [id]: state })), []);
  const [reports, setReports] = useState<Record<string, Report>>({});
  const onReport = useCallback((id: string, report: Report) => setReports((all) => ({ ...all, [id]: report })), []);
  // Prepare's "n to review" links to #review in the page: the oldest waiting attempt opens.
  const location = useLocation();
  const [linkKey, setLinkKey] = useState(0);
  useEffect(() => {
    if (location.hash === "#review") setLinkKey((key) => key + 1);
  }, [location.key, location.hash]);
  const owner = works.find((work) => states[work.id]?.ownsAct)?.id ?? null;
  const ranked = owner ?? (closeRanks ? null : works.find((work) => states[work.id]?.candidate)?.id ?? null);
  useEffect(() => { onOwnsAct(Boolean(owner)); }, [owner, onOwnsAct]);
  const hosts = works.map((work) => (
    <ReviewHost key={work.id} work={work} timezone={course.timezone} termOpen={course.term_open} roll={marks}
      ranked={ranked === work.id} quietOpen linkKey={works[0].id === work.id ? linkKey : 0} onState={onState} onReport={onReport} />
  ));
  const boxes = works.flatMap((work) => reports[work.id]?.boxes ?? []);
  const onRowOpenChange = useCallback<RowOpenChange>((box, row, open) => {
    for (const report of Object.values(reports)) report.onRowOpenChange(box, row, open);
  }, [reports]);
  return { boxes, hosts, onRowOpenChange };
}
